import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import InteractiveCalendar from '../components/InteractiveCalendar';
import axios from 'axios';
import { 
  Wallet, 
  Receipt, 
  FileCheck, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle,
  ChevronRight,
  Euro,
  Clock,
  CheckCircle,
  XCircle,
  Building,
  FileText,
  Calculator
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardComptable = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  const [stats, setStats] = useState({
    totalFactures: 0,
    facturesEnAttente: 0,
    depensesEnAttente: 0,
    depensesApprouvees: 0,
    totalEcritures: 0,
    soldeGlobal: 0
  });
  const [recentFactures, setRecentFactures] = useState([]);
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [recentEcritures, setRecentEcritures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        const [facturesRes, expensesRes, expenseStatsRes, ecrituresRes, grandLivreRes] = await Promise.all([
          axios.get(`${API}/comptabilite/factures?limit=5`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/expenses?status=pending&limit=5`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/expenses/stats`, { headers }).catch(() => ({ data: {} })),
          axios.get(`${API}/comptabilite/ecritures?limit=5`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/grand-livre/stats`, { headers }).catch(() => ({ data: {} }))
        ]);

        const factures = Array.isArray(facturesRes.data) ? facturesRes.data : [];
        const expenses = Array.isArray(expensesRes.data) ? expensesRes.data : [];
        const ecritures = Array.isArray(ecrituresRes.data) ? ecrituresRes.data : [];
        
        setRecentFactures(factures.slice(0, 5));
        setPendingExpenses(expenses.slice(0, 5));
        setRecentEcritures(ecritures.slice(0, 5));
        
        setStats({
          totalFactures: factures.length,
          facturesEnAttente: factures.filter(f => f.statut === 'en_attente').length,
          depensesEnAttente: expenseStatsRes.data?.pending || 0,
          depensesApprouvees: expenseStatsRes.data?.approved || 0,
          totalEcritures: grandLivreRes.data?.total_ecritures || 0,
          soldeGlobal: grandLivreRes.data?.solde_global || 0
        });
      } catch (error) {
        console.error('Error fetching comptable dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="comptable-dashboard">
      {isMobile && <MobileHeader title="Dashboard Comptable" onMenuClick={onMenuClick} />}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bonjour, {user?.prenom || 'Comptable'}</h1>
          <p className="text-gray-500 mt-1">Tableau de bord comptabilité</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/comptabilite')}
            className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B894] transition-colors flex items-center gap-2"
            data-testid="btn-comptabilite"
          >
            <Calculator size={18} />
            Comptabilité
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/approbation-depenses')}
          data-testid="kpi-depenses-attente"
        >
          <div className="flex items-center justify-between">
            <div className="p-2 bg-amber-50 rounded-lg">
              <Clock className="text-amber-600" size={20} />
            </div>
            <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded-full">
              En attente
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">{stats.depensesEnAttente}</p>
          <p className="text-sm text-gray-500">Dépenses à approuver</p>
        </div>

        <div 
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/approbation-depenses')}
          data-testid="kpi-depenses-approuvees"
        >
          <div className="flex items-center justify-between">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <CheckCircle className="text-emerald-600" size={20} />
            </div>
            <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-full">
              Approuvées
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">{stats.depensesApprouvees}</p>
          <p className="text-sm text-gray-500">Dépenses validées</p>
        </div>

        <div 
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/comptabilite')}
          data-testid="kpi-ecritures"
        >
          <div className="flex items-center justify-between">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FileText className="text-blue-600" size={20} />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">{stats.totalEcritures}</p>
          <p className="text-sm text-gray-500">Écritures comptables</p>
        </div>

        <div 
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
          data-testid="kpi-solde"
        >
          <div className="flex items-center justify-between">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Euro className="text-purple-600" size={20} />
            </div>
            {stats.soldeGlobal >= 0 ? (
              <TrendingUp className="text-emerald-500" size={20} />
            ) : (
              <TrendingDown className="text-red-500" size={20} />
            )}
          </div>
          <p className={`text-2xl font-bold mt-3 ${stats.soldeGlobal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatCurrency(stats.soldeGlobal)}
          </p>
          <p className="text-sm text-gray-500">Solde global</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Expenses */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileCheck className="text-[#00D3A8]" size={20} />
              <h2 className="font-semibold text-gray-900">Dépenses en attente d'approbation</h2>
            </div>
            <button 
              onClick={() => navigate('/approbation-depenses')}
              className="text-sm text-[#00D3A8] hover:underline flex items-center gap-1"
            >
              Voir tout <ChevronRight size={16} />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {pendingExpenses.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle size={40} className="mx-auto mb-2 text-emerald-400" />
                <p>Aucune dépense en attente</p>
              </div>
            ) : (
              pendingExpenses.map((expense) => (
                <div 
                  key={expense.id} 
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate('/approbation-depenses')}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{expense.description}</p>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Building size={14} />
                          {expense.building_name || 'Non assigné'}
                        </span>
                        <span>{formatDate(expense.created_at)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatCurrency(expense.amount)}</p>
                      <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                        {expense.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h2 className="font-semibold text-gray-900 mb-4">Calendrier</h2>
          <InteractiveCalendar compact={true} />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => navigate('/comptabilite')}
          className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex flex-col items-center gap-2"
          data-testid="quick-comptabilite"
        >
          <div className="p-3 bg-[#00D3A8]/10 rounded-full">
            <Wallet className="text-[#00D3A8]" size={24} />
          </div>
          <span className="font-medium text-gray-900">Comptabilité</span>
          <span className="text-xs text-gray-500">Gérer les comptes</span>
        </button>

        <button
          onClick={() => navigate('/approbation-depenses')}
          className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex flex-col items-center gap-2"
          data-testid="quick-depenses"
        >
          <div className="p-3 bg-amber-50 rounded-full">
            <FileCheck className="text-amber-600" size={24} />
          </div>
          <span className="font-medium text-gray-900">Dépenses</span>
          <span className="text-xs text-gray-500">Approuver / Rejeter</span>
        </button>

        <button
          onClick={() => navigate('/ocr-factures')}
          className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex flex-col items-center gap-2"
          data-testid="quick-ocr"
        >
          <div className="p-3 bg-blue-50 rounded-full">
            <Receipt className="text-blue-600" size={24} />
          </div>
          <span className="font-medium text-gray-900">OCR Factures</span>
          <span className="text-xs text-gray-500">Scanner des factures</span>
        </button>

        <button
          onClick={() => navigate('/grand-livre')}
          className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex flex-col items-center gap-2"
          data-testid="quick-grand-livre"
        >
          <div className="p-3 bg-purple-50 rounded-full">
            <FileText className="text-purple-600" size={24} />
          </div>
          <span className="font-medium text-gray-900">Grand Livre</span>
          <span className="text-xs text-gray-500">Voir les écritures</span>
        </button>
      </div>
    </div>
  );
};

export default DashboardComptable;
